import { useCallback, useEffect, useRef, useState } from 'react';

import APIHandler from 'config/APIHandler';

import type { DirectoryConfig, LatLng } from 'hooks/useDirectoryConfig/types';
import { useSettings } from 'hooks/useSettings';

import type { Category, FreeHotelItem, PremiumHotelItem, PremiumHotelsResponse, Subcategory } from './types';

interface UseDirectoryDataReturn {
  premiumHotelsList: PremiumHotelItem[];
  freeHotelsList: FreeHotelItem[];
  categories: Category[];
  loading: boolean;
  selectedCategory: Category | null;
  selectedSubcategory: Subcategory | null;
  query: string;
  zoom: number;
  center: LatLng;
  language: string;
  lat1: number;
  lat2: number;
  lon1: number;
  lon2: number;
  setQuery: (query: string) => void;
  setSelectedCategory: (category: Category | null) => void;
  setSelectedSubcategory: (subcategory: Subcategory | null) => void;
  setZoom: (zoom: number) => void;
  setCenter: (center: LatLng) => void;
  setMapVisible: (visible: boolean) => void;
  fetchHotels: (
    lat1: number,
    lat2: number,
    lon1: number,
    lon2: number,
    query: string,
    category: Category | null,
    subcategory: Subcategory | null,
  ) => Promise<void>;
  updateBounds: (lat1: number, lat2: number, lon1: number, lon2: number, zoom: number) => void;
  resetMap: () => void;
  resetFilters: () => void;
}

function isPremiumHotelsResponse(value: unknown): value is PremiumHotelsResponse {
  if (value === null || typeof value !== 'object' || !('items' in value)) return false;
  const { items } = value;
  return Array.isArray(items);
}

export const useDirectoryData = (config: DirectoryConfig, initialMapVisible = true): UseDirectoryDataReturn => {
  // config is guaranteed non-null — callers must wait for useDirectoryConfig to resolve before invoking this hook
  const { appId, lang } = useSettings();
  const language = lang?.langFile ?? 'en';

  const [premiumHotelsList, setPremiumHotelsList] = useState<PremiumHotelItem[]>([]);
  const [freeHotelsList, setFreeHotelsList] = useState<FreeHotelItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState<Subcategory | null>(null);

  // Tracks map visibility so fetchHotels can skip geo-filtering on narrow screens
  const mapVisibleRef = useRef(initialMapVisible);
  const setMapVisible = useCallback((visible: boolean) => {
    mapVisibleRef.current = visible;
  }, []);

  // Refs keep updateBounds from capturing stale category/subcategory closures
  const selectedCategoryRef = useRef<Category | null>(null);
  const selectedSubcategoryRef = useRef<Subcategory | null>(null);
  useEffect(() => {
    selectedCategoryRef.current = selectedCategory;
  }, [selectedCategory]);
  useEffect(() => {
    selectedSubcategoryRef.current = selectedSubcategory;
  }, [selectedSubcategory]);

  const [query, setQuery] = useState<string>('');

  const [zoom, setZoom] = useState<number>(config.map.zoom);
  const [center, setCenter] = useState<LatLng>(config.map.center);

  const [lat1, setLat1] = useState<number>(config.map.initialBounds.lat1);
  const [lat2, setLat2] = useState<number>(config.map.initialBounds.lat2);
  const [lon1, setLon1] = useState<number>(config.map.initialBounds.lon1);
  const [lon2, setLon2] = useState<number>(config.map.initialBounds.lon2);

  // Fetch categories from API on mount — stable deps (appId, config, language don't change after mount)
  useEffect(() => {
    void (async () => {
      const result: unknown = await APIHandler.getListingsCategories(appId, config.listings.table, language);
      if (result && Array.isArray(result)) {
        const cleaned = (result as Category[]).map((cat) => ({
          ...cat,
          subcategories: cat.subcategories.filter((sub) => sub.sub_catname.trim() !== ''),
        }));
        setCategories(cleaned);
      }
    })();
  }, []);

  const getCategoryParams = useCallback(
    (
      category: Category | null,
      subcategory: Subcategory | null,
    ): { categoryParam: string; subcategoryParam: string } => ({
      categoryParam: category ? `&category=${encodeURIComponent(category.cat_name)}` : '',
      subcategoryParam: subcategory ? `&subcategory=${encodeURIComponent(subcategory.sub_catname)}` : '',
    }),
    [],
  );

  const fetchHotels = useCallback(
    async (
      currentLat1: number,
      currentLat2: number,
      currentLon1: number,
      currentLon2: number,
      currentQuery: string,
      currentCategory: Category | null,
      currentSubcategory: Subcategory | null,
    ) => {
      setLoading(true);
      try {
        const { categoryParam, subcategoryParam } = getCategoryParams(currentCategory, currentSubcategory);

        const enableLocation = mapVisibleRef.current ? config.listings.location : false;

        if (config.listings.enableFreeListings) {
          const results: unknown = await APIHandler.getListings(
            appId,
            currentLon1,
            currentLon2,
            currentLat1,
            currentLat2,
            config.listings.table,
            categoryParam,
            subcategoryParam,
            currentQuery,
            enableLocation,
          );
          if (results && Array.isArray(results)) {
            setFreeHotelsList(results as FreeHotelItem[]);
          }
        }

        if (config.listings.enablePremiumListings) {
          const premium: unknown = await APIHandler.getPremiumHotelsList(
            currentLat1,
            currentLat2,
            currentLon1,
            currentLon2,
            appId,
            categoryParam,
            subcategoryParam,
            config.listings.live,
            config.listings.preview,
            enableLocation,
          );

          if (isPremiumHotelsResponse(premium)) {
            setPremiumHotelsList(premium.items);
          }
        }
      } catch {
        // listings unavailable
      } finally {
        setLoading(false);
      }
    },
    [appId, getCategoryParams, config],
  );

  // Initial fetch — always runs so listings appear immediately; on large screens the map's onIdle will
  // re-fetch with the actual visible bounds once it loads, replacing this result.
  useEffect(() => {
    void fetchHotels(
      config.map.initialBounds.lat1,
      config.map.initialBounds.lat2,
      config.map.initialBounds.lon1,
      config.map.initialBounds.lon2,
      '',
      null,
      null,
    );
  }, []);

  const updateBounds = useCallback(
    (newLat1: number, newLat2: number, newLon1: number, newLon2: number, newZoom: number) => {
      setLat1(newLat1);
      setLat2(newLat2);
      setLon1(newLon1);
      setLon2(newLon2);
      setZoom(newZoom);
      void fetchHotels(
        newLat1,
        newLat2,
        newLon1,
        newLon2,
        query,
        selectedCategoryRef.current,
        selectedSubcategoryRef.current,
      );
    },
    [fetchHotels, query],
  );

  const resetMap = useCallback(() => {
    setZoom(config.map.zoom);
    // Spread to always produce a new reference — GoogleMap only fires panTo when the prop reference changes.
    setCenter({ lat: config.map.center.lat, lng: config.map.center.lng });
  }, [config.map.center, config.map.zoom]);

  const resetFilters = useCallback(() => {
    setSelectedCategory(null);
    setSelectedSubcategory(null);
    selectedCategoryRef.current = null;
    selectedSubcategoryRef.current = null;
    void fetchHotels(lat1, lat2, lon1, lon2, query, null, null);
  }, [fetchHotels, lat1, lat2, lon1, lon2, query]);

  return {
    premiumHotelsList,
    freeHotelsList,
    categories,
    loading,
    selectedCategory,
    selectedSubcategory,
    query,
    zoom,
    center,
    language,
    lat1,
    lat2,
    lon1,
    lon2,
    setQuery,
    setSelectedCategory,
    setSelectedSubcategory,
    setZoom,
    setCenter,
    setMapVisible,
    fetchHotels,
    updateBounds,
    resetMap,
    resetFilters,
  };
};
